import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as planUtils from '../../utils/plans';
import * as authUtils from '../../utils/auth';
import * as sharingUtils from '../../utils/sharing';
import PlansView from './PlansView';
import PlanCard from './PlanCard';
import ConfirmationModal from '../ConfirmationModal';
import ShareModal from '../ShareModal';

// Component for rendering a horizontal row of plans
const PlanScroller = ({
  title,
  plansList,
  category,
  emptyMessage,
  innerRef,
  expandedSections,
  highlightedSection,
  recentCardActionsMenu,
  activeHighlightId,
  onSelect,
  onDelete,
  onDuplicate,
  onShare,
  onEditName,
  onToggleFavorite,
  toggleSectionExpanded,
  setRecentCardActionsMenu,
  preserveScroll,
  lastScrollLeftsRef
}) => {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeft(scrollLeft > 0);
    setShowRight(scrollLeft < scrollWidth - clientWidth - 10);
    if (lastScrollLeftsRef && lastScrollLeftsRef.current) {
      lastScrollLeftsRef.current[category] = scrollLeft;
    }
  };

  useEffect(() => {
    if (scrollRef.current && lastScrollLeftsRef && lastScrollLeftsRef.current && lastScrollLeftsRef.current[category] !== undefined) {
      scrollRef.current.scrollTo({ left: lastScrollLeftsRef.current[category], behavior: 'auto' });
    }
    handleScroll();
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, [plansList, expandedSections, recentCardActionsMenu]);

  if (plansList.length === 0 && !emptyMessage) return null;

  const isExpanded = expandedSections[category];
  const isHighlighted = highlightedSection === category;

  return (
    <div 
      ref={innerRef} 
      className={`mb-12 p-4 rounded-3xl transition-all duration-500 ${
        isHighlighted 
          ? 'bg-zinc-100/50 shadow-[inset_0_0_0_2px_rgba(24,24,27,0.12)]' 
          : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg sm:text-xl font-bold text-stone-900 tracking-tight flex items-center gap-2">
          <span>{title}</span>
          <span className="text-xs font-semibold text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">
            {plansList.length}
          </span>
        </h2>
        {plansList.length > 0 && (
          <button
            onClick={() => toggleSectionExpanded(category)}
            className="text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition text-stone-700 hover:text-stone-900 bg-stone-100 hover:bg-stone-200/80 px-3 py-1 rounded-full border border-stone-200/60 focus:outline-none focus:ring-0"
          >
            {isExpanded ? 'See less' : 'See all'}
            <svg 
              className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
      
      {plansList.length === 0 ? (
        <div className="text-gray-500 italic text-sm py-4">{emptyMessage}</div>
      ) : isExpanded ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 justify-items-center sm:justify-items-start">
          {plansList.map((plan) => {
            const scopedKey = `${category}:${plan.plan_id}`;
            return (
              <div key={plan.plan_id} className="w-full max-w-[280px] sm:max-w-[280px] min-w-0">
                <PlanCard
                  plan={plan}
                  onSelect={onSelect}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onShare={onShare}
                  onEditName={onEditName}
                  onToggleFavorite={onToggleFavorite}
                  showActions={recentCardActionsMenu === scopedKey}
                  onToggleActions={(planId) => {
                    preserveScroll();
                    setRecentCardActionsMenu(planId === null ? null : scopedKey);
                  }}
                  highlight={activeHighlightId === plan.plan_id}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="relative">
          <div
            ref={scrollRef}
            data-category={category}
            className="overflow-x-auto -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onScroll={handleScroll}
          >
            <style>{`.recent-plans-scroll::-webkit-scrollbar { display: none; }`}</style>
            <div className="flex gap-4 sm:gap-6 pb-2 recent-plans-scroll" style={{ minWidth: 'max-content' }}>
              {[...plansList]
                .sort((a, b) => Number(Boolean(b.is_favorite)) - Number(Boolean(a.is_favorite)))
                .slice(0, 6)
                .map((plan) => {
                const scopedKey = `${category}:${plan.plan_id}`;
                return (
                  <div key={plan.plan_id} className="flex-shrink-0" style={{ width: typeof window !== 'undefined' && window.innerWidth < 640 ? '220px' : '280px' }}>
                    <PlanCard
                      plan={plan}
                      onSelect={onSelect}
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                      onShare={onShare}
                      onEditName={onEditName}
                      onToggleFavorite={onToggleFavorite}
                      showActions={recentCardActionsMenu === scopedKey}
                      onToggleActions={(planId) => {
                        preserveScroll();
                        setRecentCardActionsMenu(planId === null ? null : scopedKey);
                      }}
                      highlight={activeHighlightId === plan.plan_id}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          {showLeft && (
            <button
              onClick={() => scrollRef.current?.scrollBy({ left: -300, behavior: 'smooth' })}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-zinc-900 hover:bg-zinc-700 shadow-lg rounded-full p-2 transition-all duration-200"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
          )}
          {showRight && (
            <button
              onClick={() => scrollRef.current?.scrollBy({ left: 300, behavior: 'smooth' })}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-zinc-900 hover:bg-zinc-700 shadow-lg rounded-full p-2 transition-all duration-200"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ModernDashboard = ({
  currentView = 'dashboard',
  onStartNewPlan,
  onLoadPlan,
  onSearch,
  searchQuery: externalSearchQuery = '',
  highlightPlanId = null
}) => {
  const { user, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sharedPlans, setSharedPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, planId: null });
  const [shareModal, setShareModal] = useState({ isOpen: false, planId: null });
  const [searchQuery, setSearchQuery] = useState(externalSearchQuery);
  const [plansWithShares, setPlansWithShares] = useState([]);
  const [recentCardActionsMenu, setRecentCardActionsMenu] = useState(null); // For Recent Plans menu state
  const [planFilter, setPlanFilter] = useState('all');

  const lastScrollTopRef = useRef(0);
  const lastScrollLeftsRef = useRef({});

  const preserveScroll = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    // 1. Capture and restore vertical scroll
    const main = document.querySelector("main[data-app-main-scroll='true']") || document.querySelector("main");
    const currentScroll = main ? main.scrollTop : 0;
    if (main && currentScroll > 0) {
      lastScrollTopRef.current = currentScroll;
    }

    // 2. Capture horizontal scrollLeft of all horizontal rows
    const horizontalScrollers = document.querySelectorAll("div[data-category]");
    horizontalScrollers.forEach(scroller => {
      const cat = scroller.getAttribute("data-category");
      if (cat) {
        lastScrollLeftsRef.current[cat] = scroller.scrollLeft;
      }
    });

    // 3. Restore vertical and horizontal scroll
    const restore = () => {
      const currentMain = document.querySelector("main[data-app-main-scroll='true']") || document.querySelector("main");
      if (currentMain && currentScroll > 0) {
        currentMain.scrollTop = currentScroll;
      }
      const currentScrollers = document.querySelectorAll("div[data-category]");
      currentScrollers.forEach(scroller => {
        const cat = scroller.getAttribute("data-category");
        if (cat && lastScrollLeftsRef.current[cat] !== undefined) {
          scroller.scrollLeft = lastScrollLeftsRef.current[cat];
        }
      });
    };

    // Run restore across multiple animation frames / timeouts to guarantee layout stability
    window.requestAnimationFrame(restore);
    setTimeout(restore, 20);
    setTimeout(restore, 50);
    setTimeout(restore, 100);
    setTimeout(restore, 150);
    setTimeout(restore, 300);
  };

  useEffect(() => {
    if (plans.length > 0 || sharedPlans.length > 0) {
      const main = document.querySelector("main[data-app-main-scroll='true']") || document.querySelector("main");
      if (main && lastScrollTopRef.current > 0) {
        const savedScroll = lastScrollTopRef.current;
        main.scrollTop = savedScroll;
        window.requestAnimationFrame(() => {
          if (main) main.scrollTop = savedScroll;
        });
        setTimeout(() => {
          if (main) main.scrollTop = savedScroll;
        }, 50);
        setTimeout(() => {
          if (main) main.scrollTop = savedScroll;
        }, 150);
      }
    }
  }, [plans, sharedPlans]);

  useEffect(() => {
    // Reset filter when switching away from plans view
    if (currentView !== 'plans') {
      setPlanFilter('all');
    }
  }, [currentView]);

  const plansLoadedRef = useRef(false);
  const plansCacheRef = useRef(null);
  const cacheTimestampRef = useRef(0);
  const lastFocusRefreshRef = useRef(0);
  const CACHE_DURATION = 30000; // 30 seconds cache

  const loadPlans = useCallback(async (forceRefresh = false, silent = false) => {
    // Don't load plans if not authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    // Check if we have cached plans that are still fresh (unless forcing refresh)
    const now = Date.now();
    if (!forceRefresh && plansCacheRef.current && (now - cacheTimestampRef.current) < CACHE_DURATION) {
      setPlans(plansCacheRef.current.owned);
      setSharedPlans(plansCacheRef.current.shared);
      setLoading(false);
      return;
    }

    // Double-check token exists and is valid before making request
    const token = authUtils.getAccessToken();
    const authHeader = authUtils.getAuthHeader();
    if (!token || !token.trim() || !authHeader.Authorization) {
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }
    try {
      const { plans: fetchedPlans, shared_plans: fetchedSharedPlans } = await planUtils.getPlansWithShares();
      // Cache the result
      plansCacheRef.current = { owned: fetchedPlans, shared: fetchedSharedPlans };
      cacheTimestampRef.current = Date.now();

      setPlans(fetchedPlans);
      setSharedPlans(fetchedSharedPlans);
      plansLoadedRef.current = true;
    } catch (error) {
      console.error('Failed to load plans:', error);
      if (error.response && error.response.status === 401) {
        // Handle unauthorized if needed
      }
      // Set empty arrays on any error
      setPlans([]);
      setSharedPlans([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);



  // Flash highlight effect
  const [activeHighlightId, setActiveHighlightId] = useState(highlightPlanId);



  useEffect(() => {
    setActiveHighlightId(highlightPlanId);
    if (highlightPlanId) {
      const timer = setTimeout(() => setActiveHighlightId(null), 5000); // 5s highlight
      return () => clearTimeout(timer);
    }
  }, [highlightPlanId]);

  useEffect(() => {
    if (isAuthenticated) {
      const token = authUtils.getAccessToken();
      const authHeader = authUtils.getAuthHeader();

      // Only proceed if we have a valid token
      if (token && authHeader.Authorization) {
        // Only load if not already loaded or cache expired
        if (!plansLoadedRef.current) {
          loadPlans();
          plansLoadedRef.current = true;
        }
      }
    } else {
      setLoading(false);
      plansLoadedRef.current = false;
      plansCacheRef.current = null;
    }
  }, [isAuthenticated, loadPlans]);

  // Reload plans when navigating to dashboard view or when reload flag is set
  useEffect(() => {
    if (isAuthenticated && currentView === 'dashboard') {
      // Check for forced reload flag
      if (window.__reloadDashboard) {
        window.__reloadDashboard = false;
        loadPlans(true); // Force refresh
        plansLoadedRef.current = true;
        return;
      }

      // Otherwise load if not loaded
      if (!plansLoadedRef.current) {
        const token = authUtils.getAccessToken();
        const authHeader = authUtils.getAuthHeader();
        if (token && authHeader.Authorization) {
          loadPlans();
          plansLoadedRef.current = true;
        }
      }
    }
  }, [currentView, isAuthenticated, loadPlans]);

  useEffect(() => {
    if (externalSearchQuery !== undefined) {
      setSearchQuery(externalSearchQuery);
    }
  }, [externalSearchQuery]);

  useEffect(() => {
    if (onSearch) {
      onSearch(searchQuery);
    }
  }, [searchQuery, onSearch]);

  // Refresh plans on window focus or visibility change to prevent stale data (throttled)
  useEffect(() => {
    if (!isAuthenticated) return;

    let isComponentMounted = true;

    const handleFocusOrVisibility = (e) => {
      if (!isComponentMounted) return;
      
      const isVisible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
      const isFocusOrPageShow = e && (e.type === 'focus' || e.type === 'pageshow');

      if (isVisible || isFocusOrPageShow) {
        const now = Date.now();
        // Only force refresh if more than 5 minutes since last focus/visibility refresh
        if (now - lastFocusRefreshRef.current > 300000) {
          lastFocusRefreshRef.current = now;
          console.log(`[ModernDashboard] Throttled auto-refresh triggered via ${e?.type || 'focus/visibility'}`);
          loadPlans(true, true); // Force refresh to bypass local cache silently
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleFocusOrVisibility);
      window.addEventListener('pageshow', handleFocusOrVisibility);
      window.addEventListener('popstate', handleFocusOrVisibility);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleFocusOrVisibility);
    }

    return () => {
      isComponentMounted = false;
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleFocusOrVisibility);
        window.removeEventListener('pageshow', handleFocusOrVisibility);
        window.removeEventListener('popstate', handleFocusOrVisibility);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleFocusOrVisibility);
      }
    };
  }, [isAuthenticated, loadPlans]);

  const handleDeletePlan = async (planId, e) => {
    if (e) e.stopPropagation();
    preserveScroll();
    setDeleteConfirm({ isOpen: true, planId });
  };

  const confirmDeletePlan = async () => {
    if (!deleteConfirm.planId) return;
    preserveScroll();
    try {
      await planUtils.deletePlan(deleteConfirm.planId);
      plansCacheRef.current = null; // Clear cache
      await loadPlans(true); // Force refresh
      setDeleteConfirm({ isOpen: false, planId: null });
      preserveScroll();
    } catch (error) {
      console.error('Failed to delete plan:', error);
      alert('Failed to delete plan. Please try again.');
      setDeleteConfirm({ isOpen: false, planId: null });
      preserveScroll();
    }
  };

  const handleBulkDelete = async (planIds) => {
    preserveScroll();
    try {
      // Delete all plans
      const deletePromises = planIds.map(planId =>
        planUtils.deletePlan(planId).catch(err => {
          console.error(`Failed to delete plan ${planId}:`, err);
          return null;
        })
      );

      await Promise.all(deletePromises);
      plansCacheRef.current = null; // Clear cache
      await loadPlans(true); // Force refresh
      preserveScroll();
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert('Failed to delete some plans. Please try again.');
      preserveScroll();
    }
  };

  const handleSharePlan = (plan) => {
    if (plan.plan_id) {
      preserveScroll();
      setShareModal({ isOpen: true, planId: plan.plan_id });
    }
  };

  const [editNameModal, setEditNameModal] = useState({ isOpen: false, plan: null, newName: '' });

  const handleEditName = (plan) => {
    preserveScroll();
    const currentName = plan.event_data?.plan_name || plan.event_data?.custom_event_type || plan.event_data?.event_type || 'Untitled Plan';
    setEditNameModal({ isOpen: true, plan, newName: currentName });
  };

  const handleSaveName = async () => {
    preserveScroll();
    if (!editNameModal.plan) return;
    const currentName = (editNameModal.plan.event_data?.plan_name || editNameModal.plan.event_data?.custom_event_type || editNameModal.plan.event_data?.event_type || 'Untitled Plan').trim();
    const newName = editNameModal.newName.trim();
    if (!newName || newName === currentName) {
      // Do NOT save and do NOT close the modal on no-change save attempt
      return;
    }

    try {
      const updatedEventData = {
        ...editNameModal.plan.event_data,
        plan_name: newName
      };
      await planUtils.updatePlan(editNameModal.plan.plan_id, {
        ...editNameModal.plan,
        event_data: updatedEventData
      });
      plansCacheRef.current = null; // Clear cache
      await loadPlans(true); // Force refresh
      setEditNameModal({ isOpen: false, plan: null, newName: '' });
      preserveScroll();
    } catch (error) {
      console.error('Failed to update plan name:', error);
      alert('Failed to update plan name. Please try again.');
      preserveScroll();
    }
  };

  const handleDuplicatePlan = async (plan) => {
    preserveScroll();
    try {
      // Get all plans to check for existing copies
      const allPlans = await planUtils.getPlans();
      const baseName = plan.event_data?.plan_name || plan.event_data?.custom_event_type || plan.event_data?.event_type || 'Untitled Plan';

      // Find existing copies
      let copyNumber = 0;
      let newName = `${baseName} (Copy)`;
      const existingNames = allPlans.map(p => p.event_data?.plan_name || p.event_data?.custom_event_type || p.event_data?.event_type || '');

      // Check if base copy exists
      if (existingNames.includes(newName)) {
        copyNumber = 1;
        // Find the highest copy number
        const copyPattern = new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\(Copy(?: \\((\\d+)\\))?\\)$`);
        existingNames.forEach(name => {
          const match = name.match(copyPattern);
          if (match) {
            const num = match[1] ? parseInt(match[1]) : 1;
            if (num >= copyNumber) {
              copyNumber = num + 1;
            }
          }
        });
        newName = copyNumber === 1 ? `${baseName} (Copy)` : `${baseName} (Copy (${copyNumber}))`;
      }

      // Create a new plan with the same data (but reset progress and update dates)
      const newPlanData = {
        event_data: {
          ...plan.event_data,
          plan_name: newName
        },
        themes: [],
        selected_theme: null,
        shopping_items: []
      };
      await planUtils.createPlan(newPlanData);
      plansCacheRef.current = null; // Clear cache
      await loadPlans(true); // Force refresh
      preserveScroll();
    } catch (error) {
      console.error('Failed to duplicate plan:', error);
      const errorMsg = error.message || 'Failed to duplicate plan. Please try again.';
      if (errorMsg.includes('credentials') || errorMsg.includes('401')) {
        alert('Authentication error. Please log out and log back in.');
      } else {
        alert(errorMsg);
      }
      preserveScroll();
    }
  };

  // Optimistic favorite toggle: flip locally first so the star animates
  // instantly, then PATCH the server. Roll back on failure.
  const handleToggleFavorite = async (plan, nextValue) => {
    const planId = plan?.plan_id;
    if (!planId) return;
    preserveScroll();
    const applyValue = (value) => {
      setPlans(prev => prev.map(p => p.plan_id === planId ? { ...p, is_favorite: value } : p));
      setSharedPlans(prev => prev.map(p => p.plan_id === planId ? { ...p, is_favorite: value } : p));
      if (plansCacheRef.current) {
        plansCacheRef.current = {
          owned: plansCacheRef.current.owned.map(p => p.plan_id === planId ? { ...p, is_favorite: value } : p),
          shared: plansCacheRef.current.shared.map(p => p.plan_id === planId ? { ...p, is_favorite: value } : p)
        };
      }
    };
    applyValue(nextValue);
    try {
      await planUtils.updatePlanFavorite(planId, { isFavorite: nextValue });
    } catch (err) {
      console.error('Failed to update favorite:', err);
      applyValue(!nextValue);
    }
  };

  const handleLoadPlanFromCard = async (plan, targetScreen = null) => {
    try {
      // Clear any transition state first
      if (window.__clearTransition) {
        window.__clearTransition();
      }
      const fullPlan = await planUtils.getPlan(plan.plan_id);
      // Pass fullPlan and the targetScreen choice directly to App.js's handleLoadPlan
      onLoadPlan(fullPlan, targetScreen);
    } catch (error) {
      console.error('Failed to load plan:', error);
      alert('Failed to load plan. Please try again.');
    }
  };

  // Add state for expanded inline view
  const [highlightedSection, setHighlightedSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    all: false,
    drafts: false,
    active: false,
    completed: false
  });

  // Section Refs for scroll targeting
  const allSectionRef = useRef(null);
  const draftsSectionRef = useRef(null);
  const activeSectionRef = useRef(null);
  const completedSectionRef = useRef(null);

  const sectionRefs = {
    all: allSectionRef,
    drafts: draftsSectionRef,
    active: activeSectionRef,
    completed: completedSectionRef
  };

  const handleMetricClick = (category) => {
    // Map 'all' category parameter safely to 'total' in stats
    const statKey = category === 'all' ? 'total' : category;
    const count = stats[statKey]?.count || 0;
    if (count === 0) return;

    const targetRef = sectionRefs[category];
    if (targetRef && targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedSection(category);
      setTimeout(() => {
        setHighlightedSection(null);
      }, 1500);
    }
  };

  const toggleSectionExpanded = (category) => {
    setExpandedSections(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Calculate stats and lists
  const stats = useMemo(() => {
    const drafts = plans.filter(p => !p.themes || p.themes.length === 0);
    const active = plans.filter(p => {
      const hasThemes = p.themes && p.themes.length > 0;
      const hasSelectedTheme = p.selected_theme;
      return hasThemes && !hasSelectedTheme;
    });
    const completed = plans.filter(p => {
      const hasSelectedTheme = p.selected_theme;
      const hasShoppingList = p.shopping_items && p.shopping_items.length > 0;
      return hasSelectedTheme && hasShoppingList;
    });

    const sortFn = (a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    const favorites = plans.filter(p => p.is_favorite);

    return {
      total: { count: plans.length, list: [...plans].sort(sortFn) },
      favorites: { count: favorites.length, list: favorites.sort(sortFn) },
      drafts: { count: drafts.length, list: drafts.sort(sortFn) },
      active: { count: active.length, list: active.sort(sortFn) },
      completed: { count: completed.length, list: completed.sort(sortFn) }
    };
  }, [plans]);

  // Fetch plans with shares (for shared view)
  useEffect(() => {
    const fetchPlansWithShares = async () => {
      if (!isAuthenticated || plans.length === 0 || currentView !== 'shared') return;

      const plansWithSharesList = [];
      for (const plan of plans) {
        try {
          const shares = await sharingUtils.listPlanShares(plan.plan_id);
          if (shares && shares.length > 0) {
            plansWithSharesList.push(plan);
          }
        } catch (error) {
          // Plan might not have shares or user might not have permission
          // Silently skip
        }
      }
      setPlansWithShares(plansWithSharesList);
    };

    if (currentView === 'shared') {
      fetchPlansWithShares();
    }
  }, [plans, isAuthenticated, currentView]);

  // Close recent plans card actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (recentCardActionsMenu) {
        // Check if click is on any card's menu button or dropdown
        const isMenuButton = e.target.closest('.plan-card-actions-menu') || e.target.closest('.plan-card-actions-menu button');
        const isDropdown = e.target.closest('.plan-card-actions-dropdown');

        // Only close if clicking outside both
        if (!isMenuButton && !isDropdown) {
          preserveScroll();
          setRecentCardActionsMenu(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [recentCardActionsMenu]);



  // Render different views
  if (currentView === 'plans') {
    return (
      <>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PlansView
            plans={plans}
            onLoadPlan={handleLoadPlanFromCard}
            onDeletePlan={handleDeletePlan}
            onDuplicatePlan={handleDuplicatePlan}
            onSharePlan={handleSharePlan}
            onEditName={handleEditName}
            onToggleFavorite={handleToggleFavorite}
            onBulkDelete={handleBulkDelete}
            searchQuery={searchQuery}
            currentFilter={planFilter}
            onFilterChange={setPlanFilter}
          />
        </div>
      </>
    );
  }

  if (currentView === 'shared') {
    return (
      <>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="ds-heading-xl mb-2">Shared Plans</h1>
            <p className="text-base ds-text-muted">Manage plans shared with you and plans you've shared with others.</p>
          </div>
          
          <div className="space-y-12">
            <div>
              <h2 className="ds-heading-lg mb-4">Shared With Me</h2>
              {sharedPlans.length === 0 ? (
                <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center text-gray-500">
                  No plans have been shared with you yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {sharedPlans.map(plan => {
                    const scopedKey = `shared-with-me:${plan.plan_id}`;
                    return (
                      <PlanCard
                        key={plan.plan_id}
                        plan={plan}
                        onSelect={handleLoadPlanFromCard}
                        onDelete={handleDeletePlan}
                        onDuplicate={handleDuplicatePlan}
                        onShare={handleSharePlan}
                        onEditName={handleEditName}
                        onToggleFavorite={handleToggleFavorite}
                        showActions={recentCardActionsMenu === scopedKey}
                        onToggleActions={(planId) => {
                          preserveScroll();
                          setRecentCardActionsMenu(planId === null ? null : scopedKey);
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <h2 className="ds-heading-lg mb-4">Shared With Others</h2>
              {plansWithShares.length === 0 ? (
                <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center text-gray-500">
                  You haven't shared any plans yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {plansWithShares.map(plan => {
                    const scopedKey = `shared-by-me:${plan.plan_id}`;
                    return (
                      <PlanCard
                        key={plan.plan_id}
                        plan={plan}
                        onSelect={handleLoadPlanFromCard}
                        onDelete={handleDeletePlan}
                        onDuplicate={handleDuplicatePlan}
                        onShare={handleSharePlan}
                        onEditName={handleEditName}
                        onToggleFavorite={handleToggleFavorite}
                        showActions={recentCardActionsMenu === scopedKey}
                        onToggleActions={(planId) => {
                          preserveScroll();
                          setRecentCardActionsMenu(planId === null ? null : scopedKey);
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Default dashboard view
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Hero Section */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-stone-100 text-stone-700 mb-2 border border-stone-200/60">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Party Studio
          </div>
          <h1 className="ds-heading-xl mb-1 text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-sm sm:text-base text-stone-500">Pick up where you left off or start planning a new celebration</p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={onStartNewPlan}
            className="ds-btn-primary px-5 py-2.5 text-sm font-bold shadow-md hover:shadow-lg transition flex items-center gap-2 rounded-xl focus:outline-none focus:ring-0 active:scale-95"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            <span>New Event Plan</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div
          onClick={() => handleMetricClick('all')}
          className={`w-full bg-white rounded-2xl border p-3.5 sm:p-4 cursor-pointer transition-all duration-200 ${
            highlightedSection === 'all'
              ? 'ring-2 ring-stone-900 border-stone-900 shadow-md scale-[1.01]'
              : 'border-stone-200/80 hover:border-stone-300 shadow-sm hover:shadow-md hover:scale-[1.01]'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs sm:text-sm font-semibold text-stone-700">Total Plans</span>
            <span className="w-2 h-2 rounded-full bg-stone-400" />
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">{stats.total.count}</div>
          <div className="text-[11px] text-stone-400 mt-1">All your party plans</div>
        </div>

        <div
          onClick={() => handleMetricClick('drafts')}
          className={`w-full bg-white rounded-2xl border p-3.5 sm:p-4 cursor-pointer transition-all duration-200 ${
            highlightedSection === 'drafts'
              ? 'ring-2 ring-amber-500 border-amber-500 shadow-md scale-[1.01]'
              : 'border-stone-200/80 hover:border-stone-300 shadow-sm hover:shadow-md hover:scale-[1.01]'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs sm:text-sm font-semibold text-stone-700">Drafts</span>
            <span className="w-2 h-2 rounded-full bg-amber-400" />
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-amber-700 tracking-tight">{stats.drafts.count}</div>
          <div className="text-[11px] text-stone-400 mt-1">Input in progress</div>
        </div>

        <div
          onClick={() => handleMetricClick('active')}
          className={`w-full bg-white rounded-2xl border p-3.5 sm:p-4 cursor-pointer transition-all duration-200 ${
            highlightedSection === 'active'
              ? 'ring-2 ring-blue-500 border-blue-500 shadow-md scale-[1.01]'
              : 'border-stone-200/80 hover:border-stone-300 shadow-sm hover:shadow-md hover:scale-[1.01]'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs sm:text-sm font-semibold text-stone-700">Active</span>
            <span className="w-2 h-2 rounded-full bg-blue-500" />
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-blue-700 tracking-tight">{stats.active.count}</div>
          <div className="text-[11px] text-stone-400 mt-1">Concept ready</div>
        </div>

        <div
          onClick={() => handleMetricClick('completed')}
          className={`w-full bg-white rounded-2xl border p-3.5 sm:p-4 cursor-pointer transition-all duration-200 ${
            highlightedSection === 'completed'
              ? 'ring-2 ring-emerald-500 border-emerald-500 shadow-md scale-[1.01]'
              : 'border-stone-200/80 hover:border-stone-300 shadow-sm hover:shadow-md hover:scale-[1.01]'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs sm:text-sm font-semibold text-stone-700">Completed</span>
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-emerald-700 tracking-tight">{stats.completed.count}</div>
          <div className="text-[11px] text-stone-400 mt-1">Full plan generated</div>
        </div>
      </div>

      {/* Studio Pro / Feature Highlight Banner */}
      <div className="mb-8 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-stone-900 via-stone-800 to-stone-900 text-white shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center text-lg shrink-0">
            ✨
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-bold text-white flex items-center gap-2">
              <span>Party Studio Pro Features</span>
              <span className="text-[10px] uppercase font-bold tracking-wider bg-amber-400/20 text-amber-300 px-2 py-0.5 rounded-full border border-amber-400/30">
                Studio AI
              </span>
            </h3>
            <p className="text-xs sm:text-sm text-stone-300 mt-0.5">
              Explore 4-Zone Spatial Moodboards, interactive Phase Timelines, and Rental Variance optimization on every event plan.
            </p>
          </div>
        </div>
        <button
          onClick={onStartNewPlan}
          className="px-4 py-2 text-xs font-bold bg-white text-stone-900 hover:bg-stone-100 rounded-xl transition shadow-xs shrink-0 self-start sm:self-auto flex items-center gap-1.5"
        >
          <span>Plan an Event</span>
          <span>→</span>
        </button>
      </div>

      {/* PlanScroller Shared Props */}
      {(() => {
        const planScrollerSharedProps = {
          expandedSections,
          highlightedSection,
          recentCardActionsMenu,
          activeHighlightId,
          onSelect: handleLoadPlanFromCard,
          onDelete: handleDeletePlan,
          onDuplicate: handleDuplicatePlan,
          onShare: handleSharePlan,
          onEditName: handleEditName,
          onToggleFavorite: handleToggleFavorite,
          toggleSectionExpanded,
          setRecentCardActionsMenu,
          preserveScroll,
          lastScrollLeftsRef
        };
        return (
          <>
            {loading ? (
              <div className="text-center py-24">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900 mb-4"></div>
                <p className="text-gray-500 font-semibold">Loading your plans...</p>
              </div>
            ) : stats.total.count === 0 && sharedPlans.length === 0 ? (
              <div className="bg-white rounded-3xl border border-gray-150 p-8 sm:p-12 text-center shadow-sm max-w-2xl mx-auto my-8 border-dashed border-gray-300">
                <div className="text-5xl sm:text-6xl mb-4 sm:mb-6">🎉</div>
                <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-2">Start your first party plan</h3>
                <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base max-w-md mx-auto">
                  Create a theme, menu, decor plan, activity list, shopping checklist, or full event plan in a few guided steps.
                </p>
                <button
                  onClick={onStartNewPlan}
                  className="ds-btn-primary px-6 py-3 text-sm font-bold shadow-md hover:shadow-lg transition flex items-center gap-2 mx-auto focus:outline-none focus:ring-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Start New Plan
                </button>
              </div>
            ) : (
              <>
                {/* Favorites rail — only shown when the user has at least one
                    starred plan. Pinned plans are the very first thing they see. */}
                {stats.favorites.count > 0 && (
                  <PlanScroller title="Favorites" category="favorites" plansList={stats.favorites.list} {...planScrollerSharedProps} />
                )}
                <PlanScroller 
                  title="All Plans" 
                  category="all" 
                  plansList={stats.total.list} 
                  emptyMessage="Create your first plan to get started." 
                  innerRef={allSectionRef} 
                  {...planScrollerSharedProps}
                />
                <PlanScroller 
                  title="Drafts" 
                  category="drafts" 
                  plansList={stats.drafts.list} 
                  innerRef={draftsSectionRef} 
                  {...planScrollerSharedProps}
                />
                <PlanScroller 
                  title="Active Plans" 
                  category="active" 
                  plansList={stats.active.list} 
                  innerRef={activeSectionRef} 
                  {...planScrollerSharedProps}
                />
                <PlanScroller 
                  title="Completed Plans" 
                  category="completed" 
                  plansList={stats.completed.list} 
                  innerRef={completedSectionRef} 
                  {...planScrollerSharedProps}
                />

                {/* Shared Plans Section */}
                {sharedPlans.length > 0 && (
                  <PlanScroller title="Shared With Me" category="shared" plansList={sharedPlans} {...planScrollerSharedProps} />
                )}
              </>
            )}
          </>
        );
      })()}

      {/* Modals */}
      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, planId: null })}
        onConfirm={confirmDeletePlan}
        title="Delete Plan?"
        message="Are you sure you want to delete this plan? This action cannot be undone and all associated data will be permanently removed."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {shareModal.isOpen && (
        <ShareModal
          isOpen={shareModal.isOpen}
          onClose={() => setShareModal({ isOpen: false, planId: null })}
          planId={shareModal.planId}
        />
      )}

      {/* Edit Name Modal */}
      {(() => {
        if (!editNameModal.isOpen) return null;
        const currentPlanName = editNameModal.plan ? (editNameModal.plan.event_data?.plan_name || editNameModal.plan.event_data?.custom_event_type || editNameModal.plan.event_data?.event_type || 'Untitled Plan').trim() : '';
        const isSaveDisabled = !editNameModal.newName.trim() || editNameModal.newName.trim() === currentPlanName;
        return (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
            onClick={() => setEditNameModal({ isOpen: false, plan: null, newName: '' })}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--brand-charcoal)' }}>Edit Plan Name</h3>
              <input
                type="text"
                value={editNameModal.newName}
                onChange={(e) => setEditNameModal({ ...editNameModal, newName: e.target.value })}
                className="ds-input mb-4"
                placeholder="Plan name"
                autoFocus={typeof window !== 'undefined' ? window.innerWidth >= 768 : false}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!isSaveDisabled) handleSaveName();
                  } else if (e.key === 'Escape') {
                    setEditNameModal({ isOpen: false, plan: null, newName: '' });
                  }
                }}
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setEditNameModal({ isOpen: false, plan: null, newName: '' })}
                  className="ds-btn-ghost px-4 py-2 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveName}
                  disabled={isSaveDisabled}
                  className="ds-btn-primary px-4 py-2 text-sm"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ModernDashboard;
